import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { Loader2 } from 'lucide-react'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderFilters, PageContent } from '@/components/ui/page'
import { TooltipProvider } from '@/components/ui/tooltip'
import { IconNumberBadge } from '@/components/ui/icon-number-badge'
import { ActivityCard } from '@/components/ActivityCard'
import { ActivityFilters } from '@/components/ActivityFilters'
import { useActivities, type ActivityFilters as Filters } from '@/hooks/useActivities'
import { useActivityStats } from '@/hooks/useActivityStats'
import { ACTIVITY_TYPE_CONFIG } from '@/utils/activityHelpers'

export const Route = createFileRoute('/activities')({
  component: Activities,
  errorComponent: RouteErrorFallback,
})

function Activities() {
  const [filters, setFilters] = useState<Filters>({
    acknowledged: false,
    types: [],
    startDate: null,
    endDate: null
  })

  // Data fetching
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError
  } = useActivities(filters)

  const { data: stats } = useActivityStats()

  // Infinite scroll
  const { ref: loadMoreRef } = useInView({
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    }
  })

  // Flatten pages
  const activities = data?.pages.flatMap(page => page.data) ?? []

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Aktivität</PageHeaderHeading>
              <PageHeaderDescription>Benachrichtigungen und Systemereignisse</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderFilters>
              {/* Compact stats badges */}
              {stats && stats.totalUnacknowledged > 0 && (
                <TooltipProvider delayDuration={300}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Anzahl:</span>
                    {Object.entries(stats.byType).map(([type, count]) => {
                      const config = ACTIVITY_TYPE_CONFIG[type]
                      if (!config || count === 0) return null
                      return (
                        <IconNumberBadge
                          key={type}
                          icon={config.icon}
                          count={count}
                          label={config.label}
                        />
                      )
                    })}
                  </div>
                </TooltipProvider>
              )}
              <ActivityFilters filters={filters} onFiltersChange={setFilters} />
            </PageHeaderFilters>
          </PageHeaderContent>
        </PageHeader>

        {/* Content area - scrollable */}
        <PageContent className="p-6 overflow-auto">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-muted-foreground">Lade Aktivitäten...</span>
            </div>
          )}

          {/* Error state */}
          {isError && (
            <div className="text-center py-12">
              <p className="text-red-500">Fehler beim Laden der Aktivitäten</p>
              <p className="text-sm text-muted-foreground mt-2">
                Bitte versuchen Sie es später erneut
              </p>
            </div>
          )}

          {/* Activity cards */}
          {!isLoading && !isError && (
            <div className="space-y-2">
              {activities.map(activity => (
                <ActivityCard key={activity.id} activity={activity} />
              ))}
            </div>
          )}

          {/* Infinite scroll trigger */}
          {!isLoading && !isError && activities.length > 0 && (
            <div ref={loadMoreRef} className="py-8 text-center">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-muted-foreground">Lade weitere Aktivitäten...</span>
                </div>
              )}
              {!hasNextPage && (
                <p className="text-sm text-muted-foreground">
                  Keine weiteren Aktivitäten vorhanden
                </p>
              )}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isError && activities.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-muted-foreground mb-2">Keine Aktivitäten gefunden</p>
              <p className="text-sm text-muted-foreground">
                {filters.acknowledged === false
                  ? 'Es gibt keine ungelesenen Aktivitäten'
                  : filters.types && filters.types.length > 0
                  ? 'Keine Aktivitäten für die ausgewählten Filter'
                  : 'Es wurden noch keine Aktivitäten aufgezeichnet'}
              </p>
            </div>
          )}
        </PageContent>
      </PageCard>
    </PageContainer>
  )
}
